import { useState } from 'react';
import { Users, X, Copy, Check } from 'lucide-react';
import { usePartners } from '@/hooks/usePartners';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * «Едим вместе» — управление партнёрами.
 *
 * Код тот же, что для приглашений: человеку незачем помнить два разных.
 * Связь взаимная сразу, без «подтверди заявку» — код можно узнать только
 * от самого партнёра, так что лишний шаг ничего не защищает.
 */
export function PartnersCard() {
  const { profile } = useUserStore();
  const { partners, addByCode, remove, setAutoAccept } = usePartners();
  const { haptic } = useTelegram();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const myCode = (profile as any)?.ref_code as string | undefined;

  async function add() {
    if (!code.trim() || busy) return;
    setBusy(true);
    setMsg(null);
    const r = await addByCode(code.trim());
    setBusy(false);
    if (r.ok) {
      haptic('success');
      setCode('');
      setMsg(`${r.name || 'Партнёр'} добавлен`);
    } else {
      haptic('error');
      setMsg(
        r.error === 'not_found' ? 'Код не найден'
        : r.error === 'self' ? 'Это твой собственный код'
        : r.error === 'limit' ? 'Больше трёх партнёров нельзя'
        : 'Не получилось, попробуй ещё раз',
      );
    }
    setTimeout(() => setMsg(null), 3000);
  }

  function copyMine() {
    if (!myCode) return;
    navigator.clipboard?.writeText(myCode).then(() => {
      haptic('light');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div className="bg-bg-card rounded-2xl p-4 mb-4 border border-line">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-sky-glow" aria-hidden />
        <span className="text-sm font-medium text-ink">Едим вместе</span>
      </div>
      <p className="text-[11px] text-ink-dim mb-3 leading-snug">
        Если вы питаетесь одинаково, записывать может кто-то один — второму блюдо
        прилетит в одно касание.
      </p>

      {partners.length > 0 && (
        <div className="space-y-2 mb-3">
          {partners.map((p) => (
            <div key={p.id} className="flex items-center gap-2 bg-bg-elevated/50 rounded-xl px-3 py-2">
              <span className="text-sm text-ink flex-1 truncate">{p.name || 'Без имени'}</span>
              <button
                onClick={() => {
                  haptic('light');
                  setAutoAccept(p.id, !p.auto_accept);
                }}
                className={`text-[10px] px-2 py-1 rounded-lg border transition-colors ${
                  p.auto_accept
                    ? 'text-sky-glow bg-sky-glow/10 border-sky-glow/30'
                    : 'text-ink-dim border-line'
                }`}
              >
                {p.auto_accept ? 'принимаю сам' : 'спрашивать'}
              </button>
              <button
                onClick={() => remove(p.id)}
                aria-label="Убрать партнёра"
                className="text-ink-dim p-1 -m-1"
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      {partners.length < 3 && (
        <div className="flex gap-2 mb-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Код партнёра"
            className="flex-1 bg-bg border border-line rounded-xl px-3 py-2 text-sm placeholder:text-ink-dim outline-none focus:border-sky-glow/50 tracking-wider"
          />
          <button
            onClick={add}
            disabled={busy || !code.trim()}
            className="px-4 rounded-xl bg-sky-glow/15 border border-sky-glow/30 text-sky-glow text-sm active:scale-95 transition disabled:opacity-40"
          >
            Связать
          </button>
        </div>
      )}

      {msg && <div className="text-[11px] text-ink-muted mb-2">{msg}</div>}

      {myCode && (
        <button
          onClick={copyMine}
          className="w-full flex items-center justify-center gap-1.5 text-[11px] text-ink-dim py-1.5 active:opacity-70"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Скопирован' : `Твой код: ${myCode}`}
        </button>
      )}

      <p className="text-[10px] text-ink-dim leading-snug mt-1">
        «Принимаю сам» — блюда партнёра появляются в дневнике без подтверждения.
      </p>
    </div>
  );
}
